// src/pages/PreviewGalleryPage.tsx
// Vitrine pública — galeria de campos/decks públicos
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { BookOpen, ArrowRight, Search } from 'lucide-react';
import './PreviewGalleryPage.css';

interface DeckSummary {
    deckId: string;
    deckName: string;
    deckDescription: string;
    iconUrl: string | null;
    totalSubdecks: number;
    totalCards: number;
    publicSlug: string;
}

interface FieldGroup {
    fieldName: string;
    totalDecks: number;
    totalCards: number;
    decks: DeckSummary[];
}

async function fetchPreview(): Promise<FieldGroup[]> {
    const res = await fetch('/api/public/preview');
    if (!res.ok) throw new Error('Erro ao carregar vitrine');
    return res.json();
}

const PreviewGalleryPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('ref');

    const [search, setSearch] = useState('');

    const { data: fields = [], isLoading } = useQuery({
        queryKey: ['public-preview'],
        queryFn: fetchPreview,
        staleTime: 5 * 60 * 1000,
    });

    const searchLower = search.toLowerCase();
    const filtered: FieldGroup[] = searchLower
        ? fields.map((f: FieldGroup) => ({
            ...f,
            decks: f.decks.filter((d: DeckSummary) =>
                d.deckName.toLowerCase().includes(searchLower) ||
                f.fieldName.toLowerCase().includes(searchLower)
            )
        })).filter((f: FieldGroup) => f.decks.length > 0)
        : fields;

    const totalDecks = fields.reduce((s: number, f: FieldGroup) => s + f.totalDecks, 0);
    const totalCards = fields.reduce((s: number, f: FieldGroup) => s + f.totalCards, 0);

    return (
        <div className="cpv-root" data-theme="light">
            {/* Hero */}
            <header className="cpv-hero">
                <div className="cpv-hero-inner">
                    <div className="cpv-logo">GRAIA</div>
                    <h1 className="cpv-hero-title">
                        O maior banco de flashcards de medicina do Brasil
                    </h1>
                    <p className="cpv-hero-sub">
                        {totalDecks} módulos · {totalCards.toLocaleString('pt-BR')} flashcards
                    </p>
                    <div className="cpv-hero-actions">
                        <a href="/cadastro" className="cpv-cta-primary">
                            Criar conta grátis
                        </a>
                        <a href="/login" className="cpv-cta-secondary">
                            Já tenho conta
                        </a>
                    </div>
                </div>
            </header>

            {/* Search */}
            <div className="cpv-search-bar">
                <div className="cpv-search-inner">
                    <Search size={18} />
                    <input
                        placeholder="Buscar módulo…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="cpv-search-input"
                    />
                </div>
            </div>

            {/* Content */}
            <main className="cpv-main">
                {isLoading && (
                    <div className="cpv-loading">Carregando conteúdo…</div>
                )}

                {!isLoading && filtered.length === 0 && (
                    <div className="cpv-empty">Nenhum módulo disponível para visualização no momento.</div>
                )}

                {filtered.map(field => (
                    <section key={field.fieldName} className="cpv-field-section">
                        <h2 className="cpv-field-title">{field.fieldName}</h2>
                        <div className="cpv-deck-grid">
                            {field.decks.map(deck => (
                                <Link
                                    key={deck.deckId}
                                    to={`/preview/${deck.publicSlug}${ref ? `?ref=${ref}` : ''}`}
                                    className="cpv-deck-card"
                                >
                                    <div className="cpv-deck-card-top">
                                        {deck.iconUrl
                                            ? <img src={deck.iconUrl} alt="" className="cpv-deck-icon" />
                                            : <div className="cpv-deck-icon-placeholder"><BookOpen size={28} /></div>
                                        }
                                    </div>
                                    <div className="cpv-deck-card-body">
                                        <h3 className="cpv-deck-name">{deck.deckName}</h3>
                                        {deck.deckDescription && (
                                            <p className="cpv-deck-desc">{deck.deckDescription}</p>
                                        )}
                                        <div className="cpv-deck-meta">
                                            <span>{deck.totalSubdecks} submódulos</span>
                                            <span>·</span>
                                            <span>{deck.totalCards.toLocaleString('pt-BR')} flashcards</span>
                                        </div>
                                    </div>
                                    <div className="cpv-deck-card-footer">
                                        <span>Ver conteúdo</span>
                                        <ArrowRight size={15} />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                ))}
            </main>

            {/* CTA fixo */}
            <div className="cpv-sticky-cta">
                <span>Acesse todos os módulos com uma conta GRAIA</span>
                <a href="/cadastro" className="cpv-cta-primary sm">
                    Começar agora — é grátis
                </a>
            </div>
        </div>
    );
};

export default PreviewGalleryPage;
